import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { tenantApi } from '../../services/api';
import { useTenantContext } from '../../context/TenantContext';
import socketService from '../../services/socket';
import ExecutiveSummaryCard from '../../components/msp/ExecutiveSummaryCard';
import PrioritizedFixList from '../../components/msp/PrioritizedFixList';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

function TenantDetail() {
  const { tenantId } = useParams();
  const { setSelectedTenantId } = useTenantContext();
  const [dashboard, setDashboard] = useState(null);
  const [status, setStatus] = useState('loading');
  const [scheduleFrequency, setScheduleFrequency] = useState('monthly');
  const [scheduleWindow, setScheduleWindow] = useState('overnight');
  const [scheduleStatus, setScheduleStatus] = useState('idle');
  const [scanOptions, setScanOptions] = useState({
    scope: 'external',
    intensity: 'standard',
  });
  const [triggerStatus, setTriggerStatus] = useState('idle');
  const [baselineScan, setBaselineScan] = useState(null);

  useEffect(() => {
    setSelectedTenantId(tenantId);
  }, [tenantId, setSelectedTenantId]);

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');

    Promise.all([
      tenantApi.getTenantDashboard(tenantId),
      tenantApi.getScanConfig(tenantId).catch(() => ({ data: { scan_config: {} } })),
      tenantApi.getBaselineScan(tenantId).catch(() => ({ data: {} })),
    ])
      .then(([dashboardResponse, configResponse, baselineResponse]) => {
        if (!isMounted) return;
        setDashboard(dashboardResponse.data);
        const schedule = dashboardResponse.data?.scan_schedule || dashboardResponse.data?.scanSchedule;
        if (schedule) {
          setScheduleFrequency(schedule.frequency || 'monthly');
          setScheduleWindow(schedule.window || 'overnight');
        }
        const config = configResponse.data?.scan_config || {};
        setScanOptions({
          scope: config.scope || 'external',
          intensity: config.intensity || 'standard',
        });
        setBaselineScan(baselineResponse.data?.baseline_scan ?? null);
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  useEffect(() => {
    socketService.connect();
    const refreshOnCompletion = (data) => {
      const tenantId = data?.tenant_id;
      if (tenantId && tenantId !== tenantId) return;
      tenantApi.getTenantDashboard(tenantId).then((response) => {
        setDashboard(response.data);
      });
    };
    socketService.on('externalScanCompleted', refreshOnCompletion);
    socketService.on('externalScanFailed', refreshOnCompletion);

    return () => {
      socketService.off('externalScanCompleted', refreshOnCompletion);
      socketService.off('externalScanFailed', refreshOnCompletion);
    };
  }, [tenantId]);

  const tenantName = dashboard?.tenant_name || dashboard?.name || tenantId;
  const trendData = useMemo(() => {
    return (
      dashboard?.trend ||
      dashboard?.risk_trend ||
      [
        { label: '30d', value: dashboard?.risk_score || 0 },
        { label: '60d', value: dashboard?.risk_score || 0 },
        { label: '90d', value: dashboard?.risk_score || 0 },
      ]
    );
  }, [dashboard]);

  const priorities = dashboard?.top_priorities || dashboard?.priorities || [];
  const changeEvents = dashboard?.recent_changes || dashboard?.change_events || [];
  const scanStatus = dashboard?.scan_status || dashboard?.last_scan_status || 'unknown';
  const nextScanAt = dashboard?.next_scan_at;
  const nextScanLabel = nextScanAt
    ? new Date(nextScanAt).toLocaleString()
    : dashboard?.next_scan || 'Not scheduled';
  const assetSummary = dashboard?.asset_summary || dashboard?.assets_summary || {};
  const severitySummary = dashboard?.findings_by_severity || dashboard?.severity_breakdown || {};
  const reportComparison = dashboard?.report_comparison || dashboard?.report_delta;

  const handleTriggerScan = async () => {
    setTriggerStatus('submitting');
    try {
      await tenantApi.triggerExternalScan(tenantId, {
        scope: scanOptions.scope,
        intensity: scanOptions.intensity,
      });
      setTriggerStatus('success');
      setTimeout(() => setTriggerStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to trigger external scan', error);
      setTriggerStatus('error');
    }
  };

  const handleScheduleSave = async (event) => {
    event.preventDefault();
    setScheduleStatus('submitting');
    try {
      await tenantApi.updateScanSchedule(tenantId, {
        frequency: scheduleFrequency,
        window: scheduleWindow,
      });
      setScheduleStatus('success');
    } catch (error) {
      console.error('Failed to update schedule', error);
      setScheduleStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName={tenantName}
        subtitle="Tenant security posture and operational data"
      >
        <div className="msp-inline-form" style={{ gap: '8px', marginRight: '12px' }}>
          <select
            className="msp-input"
            value={scanOptions.scope}
            onChange={(e) => setScanOptions({ ...scanOptions, scope: e.target.value })}
            style={{ minWidth: '120px' }}
          >
            <option value="external">External</option>
            <option value="full">Full Surface</option>
          </select>
          <select
            className="msp-input"
            value={scanOptions.intensity}
            onChange={(e) => setScanOptions({ ...scanOptions, intensity: e.target.value })}
            style={{ minWidth: '120px' }}
          >
            <option value="light">Light</option>
            <option value="standard">Standard</option>
            <option value="deep">Deep</option>
          </select>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleTriggerScan}
          disabled={triggerStatus === 'submitting'}
        >
          {triggerStatus === 'submitting' ? 'Starting…' : 'Trigger External Scan'}
        </button>
      </TenantHeader>

      {status === 'loading' && <div className="msp-empty">Loading tenant telemetry…</div>}

      {status === 'error' && <div className="msp-empty">Unable to load tenant dashboard.</div>}
      {triggerStatus === 'success' && (
        <div className="msp-empty" style={{ color: 'var(--neon-cyan)' }}>
          Scan triggered successfully with {scanOptions.scope} scope and {scanOptions.intensity} intensity.
        </div>
      )}
      {triggerStatus === 'error' && (
        <div className="msp-empty" style={{ color: '#ff4d6d' }}>
          Failed to trigger scan. Please try again.
        </div>
      )}

      {status === 'ready' && (
        <>
          <div className="msp-grid">
            <div className="msp-card">
              <span className="msp-card-label">Risk Score</span>
              <span className="msp-card-value">{dashboard?.risk_score || dashboard?.riskScore || '—'}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Last Scan Status</span>
              <span className="msp-card-value">{scanStatus}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Next Scheduled Scan</span>
              <span className="msp-card-value">{nextScanLabel}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Top External Assets</span>
              <span className="msp-card-value">{assetSummary.top_assets || assetSummary.topAssets || '—'}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Baseline Scan</span>
              <span className="msp-card-value">
                {baselineScan ? (
                  <>
                    Scan #{baselineScan.id} &middot; {baselineScan.started_at || baselineScan.completed_at
                      ? new Date(baselineScan.started_at || baselineScan.completed_at).toLocaleDateString()
                      : '—'}
                    <br />
                    <Link to={`/msp/tenants/${tenantId}/external-scans/${baselineScan.id}`} style={{ fontSize: '12px' }}>View baseline</Link>
                    {' · '}
                    <Link to={`/msp/tenants/${tenantId}/external-scans`} style={{ fontSize: '12px' }}>Change</Link>
                  </>
                ) : (
                  'Not set'
                )}
              </span>
            </div>
          </div>

          <div className="msp-grid">
            <ExecutiveSummaryCard
              summary={dashboard?.executive_summary}
              changes={dashboard?.change_summary}
              nextSteps={dashboard?.next_steps}
            />
            <PrioritizedFixList items={priorities.slice(0, 5)} />
          </div>

          <div className="msp-grid">
            <div className="msp-card">
              <span className="msp-card-label">Findings by Severity</span>
              <div className="msp-list">
                {Object.keys(severitySummary).length > 0 ? (
                  Object.entries(severitySummary).map(([severity, count]) => (
                    <div key={severity} className="msp-list-item">
                      <span className="msp-list-title">{severity}</span>
                      <span className="msp-badge msp-badge-muted">{count}</span>
                    </div>
                  ))
                ) : (
                  <div className="msp-empty">No severity data yet.</div>
                )}
              </div>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Exposed Services</span>
              <span className="msp-card-value">{assetSummary.exposed_services || assetSummary.exposedServices || '—'}</span>
              <span className="msp-card-label">Open Critical Findings</span>
              <span className="msp-card-value">
                {severitySummary.critical || severitySummary.high || 0}
              </span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Report Comparison</span>
              <span className="msp-card-value">
                {reportComparison?.summary || reportComparison?.delta || 'No comparison yet'}
              </span>
              {reportComparison?.changes && (
                <div className="msp-card-label">Changes: {reportComparison.changes}</div>
              )}
            </div>
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Scan Schedule</div>
            <form className="msp-form" onSubmit={handleScheduleSave}>
              <label className="msp-form-field">
                Frequency
                <select
                  className="msp-input"
                  value={scheduleFrequency}
                  onChange={(event) => setScheduleFrequency(event.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </label>
              <label className="msp-form-field">
                Time Window
                <select
                  className="msp-input"
                  value={scheduleWindow}
                  onChange={(event) => setScheduleWindow(event.target.value)}
                >
                  <option value="overnight">Overnight</option>
                  <option value="business-hours">Business Hours</option>
                  <option value="weekend">Weekend</option>
                </select>
              </label>
              <button type="submit" className="btn btn-primary" disabled={scheduleStatus === 'submitting'}>
                {scheduleStatus === 'submitting' ? 'Saving…' : 'Save Schedule'}
              </button>
            </form>
            {scheduleStatus === 'error' && <div className="msp-empty">Unable to save schedule.</div>}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Risk Trend</div>
            <div className="msp-chart-card">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="riskTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00f3ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#00f3ff" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" stroke="#8a94a6" />
                  <YAxis stroke="#8a94a6" />
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#00f3ff" fill="url(#riskTrend)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Latest Change Events</div>
            {changeEvents.length === 0 ? (
              <div className="msp-empty">No change events recorded yet.</div>
            ) : (
              <div className="msp-timeline">
                {changeEvents.slice(0, 5).map((event) => (
                  <div key={event.id || event.timestamp} className="msp-timeline-item">
                    <div className="msp-timeline-title">{event.title || event.change_type}</div>
                    <div className="msp-timeline-meta">
                      <span>{event.timestamp || event.detected_at}</span>
                      <span>{event.impact || event.summary}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TenantDetail;
